import React from 'react';
import { motion } from 'framer-motion';
import { Search, Bot, KanbanSquare, Table, Users, BarChart3, MessageSquare, Check, FileText } from 'lucide-react';
import Monitor from './Monitor';

export const ResearchHubAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <Monitor width="w-48" height="h-32">
                <div className="w-full h-full bg-slate-900 p-3 flex flex-col gap-2">
                    {/* Search Bar */}
                    <div className="w-full h-6 bg-slate-800 rounded-full flex items-center px-2 gap-2 border border-slate-700">
                        <Search className="w-3 h-3 text-slate-400" />
                        <div className="w-20 h-1.5 bg-slate-700 rounded-full" />
                    </div>

                    {/* Results */}
                    <div className="flex-1 flex flex-col gap-1.5 overflow-hidden">
                        {[1, 2, 3].map((i) => (
                            <motion.div
                                key={i}
                                className="w-full h-6 bg-slate-800/50 rounded flex items-center px-2 gap-2"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.2 + 0.5, duration: 0.5 }}
                            >
                                <div className="w-4 h-4 bg-purple-500/20 rounded flex items-center justify-center">
                                    <FileText className="w-2.5 h-2.5 text-purple-400" />
                                </div>
                                <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full" />
                            </motion.div>
                        ))}
                    </div>
                </div>

            </Monitor>
        </div>
    );
};

export const VirtualAssistantAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <Monitor width="w-48" height="h-32">
                <div className="w-full h-full bg-slate-900 p-3 flex flex-col justify-end gap-2">
                    {/* Messages */}
                    <motion.div
                        className="self-end bg-blue-600/80 text-white text-[8px] px-2 py-1 rounded-lg rounded-tr-none max-w-[80%]"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        Analyze Q3 report
                    </motion.div>

                    <motion.div
                        className="self-start bg-slate-800 text-slate-200 text-[8px] px-2 py-1 rounded-lg rounded-tl-none max-w-[80%] border border-slate-700"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8 }}
                    >
                        Processing data... Revenue up 15%
                    </motion.div>

                    {/* Input area */}
                    <div className="w-full h-5 bg-slate-800 rounded-full mt-1 border border-slate-700 flex items-center px-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    </div>
                </div>

            </Monitor>
        </div>
    );
};

export const ProjectManagementAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <Monitor width="w-48" height="h-32">
                <div className="w-full h-full bg-slate-900 p-2 flex gap-2">
                    {/* Columns */}
                    {[0, 1, 2].map((col) => (
                        <div key={col} className="flex-1 bg-slate-800/50 rounded flex flex-col gap-1.5 p-1">
                            <div className="w-8 h-1.5 bg-slate-700 rounded mb-1" />
                            {[0, 1].map((card) => (
                                <motion.div
                                    key={card}
                                    className="w-full h-6 bg-slate-700 rounded border border-slate-600"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: col * 0.2 + card * 0.1 }}
                                />
                            ))}
                            {col === 1 && (
                                <motion.div
                                    className="w-full h-6 bg-blue-600/80 rounded border border-blue-500/50"
                                    initial={{ x: -50, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 1.5, duration: 0.5 }}
                                />
                            )}
                        </div>
                    ))}
                </div>

            </Monitor>
        </div>
    );
};

export const DataframesAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <Monitor width="w-48" height="h-32">
                <div className="w-full h-full bg-slate-900 p-2">
                    {/* Grid */}
                    <div className="grid grid-cols-4 gap-0.5">
                        {Array.from({ length: 16 }).map((_, i) => (
                            <motion.div
                                key={i}
                                className={`h-4 rounded-sm border border-slate-700/50 ${i < 4 ? 'bg-slate-800' : 'bg-slate-900'
                                    }`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: i * 0.05 }}
                            >
                                {i === 9 && (
                                    <motion.div
                                        className="w-full h-full bg-blue-500/30"
                                        animate={{ opacity: [0.3, 1, 0.3] }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>

            </Monitor>
        </div>
    );
};

export const RoleBasedPortalsAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center relative">
            {/* Background Cards */}
            <motion.div
                className="absolute"
                animate={{ x: -40, y: -10, scale: 0.9, opacity: 0.6 }}
            >
                <Monitor width="w-32" height="h-20" borderColor="border-slate-700">
                    <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                        <Users className="w-6 h-6 text-slate-600" />
                    </div>
                </Monitor>
            </motion.div>

            <motion.div
                className="absolute"
                animate={{ x: 40, y: -10, scale: 0.9, opacity: 0.6 }}
            >
                <Monitor width="w-32" height="h-20" borderColor="border-slate-700">
                    <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                        <Users className="w-6 h-6 text-slate-600" />
                    </div>
                </Monitor>
            </motion.div>

            {/* Main Card */}
            <div className="z-10">
                <Monitor width="w-40" height="h-28">
                    <div className="w-full h-full bg-slate-900 flex flex-col items-center justify-center gap-2">
                        <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center border border-orange-500/50">
                            <Users className="w-4 h-4 text-orange-500" />
                        </div>
                        <div className="w-20 h-2 bg-slate-700 rounded-full" />
                        <div className="w-14 h-2 bg-slate-800 rounded-full" />
                    </div>
                </Monitor>
            </div>
        </div>
    );
};

export const BillingAnalyticsAnimation = () => {
    return (
        <div className="w-full h-full flex items-center justify-center">
            <Monitor width="w-48" height="h-32">
                <div className="w-full h-full bg-slate-900 p-3 flex items-end justify-between gap-1">
                    {[30, 50, 40, 70, 60, 90].map((height, i) => (
                        <motion.div
                            key={i}
                            className="w-full bg-cyan-500/80 rounded-t-sm"
                            initial={{ height: 0 }}
                            whileInView={{ height: `${height}%` }}
                            transition={{ duration: 1, delay: i * 0.1 }}
                        />
                    ))}
                </div>

            </Monitor>
        </div>
    );
};
